import React, { Component } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import debounce from 'lodash.debounce';
import {
  Button,
  Radio,
  message,
  Spin,
} from 'antd';
import {
  Check,
  Copy,
  EnvelopeSimple,
  FilePdf,
  LinkSimple,
  Phone,
  WarningCircle,
} from '@phosphor-icons/react';

import ModalWrapper from './ModalWrapper';
import InvitationDeliveryBadges from './InvitationDeliveryBadges';
import Input from '../Input';
import Typography from '../Typography';
import { validSendEmail } from '../../global/validation';
import csrfHeaders from '../../utils/csrfHeaders';
import {
  canResendInvitation,
  INVALID_FAX_NUMBER_ERROR,
  isActionablePendingInvitation,
  normalizeFaxNumber,
} from './invitationUtils';

import './invitePartnerModal.scss';

class InvitePartnerModal extends Component {
  // Pick the fax number to seed the form with. When the referral came in by
  // fax, the sender's number is almost always who the user wants to fax the
  // invitation back to — but never overwrite something the user has already
  // typed.
  static deriveFaxNumberFromCheck(prevState, checkData) {
    if (checkData?.fax_delivery_available === false) return '';
    if (prevState.faxNumberTouched) return prevState.faxNumber;
    if (prevState.faxNumber) return prevState.faxNumber;
    return checkData?.sender_fax || prevState.faxNumber;
  }

  // handleChange fires per-keystroke; debounce so typing "alice@example.com"
  // doesn't burst N /check requests before the user even finishes the field.
  debouncedCheckRecordInvitation = debounce(() => {
    if (this.mounted) this.checkRecordInvitation();
  }, 300);

  constructor(props) {
    super(props);

    this.state = {
      email: '',
      name: '',
      companyName: '',
      deliveryMethod: 'email',
      faxNumber: this.props.modalType?.data?.senderFax || '',
      checking: false,
      loading: false,
      error: null,
      existingCheck: null,
      recordCheck: null,
      // Latched from any successful /check response so it survives subsequent
      // resets of existingCheck/recordCheck (e.g. after revoke + empty-email
      // refetch where checkExisting no-ops and leaves both null). Once the
      // org's fax capability is known for this modal session, treat it as
      // sticky rather than re-deriving from possibly-null caches.
      cachedFaxDeliveryAvailable: null,
      sentInvitation: null,
      deliveryFailed: false,
      inviteLink: null,
      copied: false,
      reinstated: false,
      addFaxForFutureMatching: true,
      faxNumberTouched: false,
      previewedInvitationId: null,
      generatingPreview: false,
      sendingPreviewedFax: false,
    };
  }

  componentDidMount() {
    this.mounted = true;
    this.checkRecordInvitation();
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.copyTimer) clearTimeout(this.copyTimer);
    if (this.debouncedCheckRecordInvitation) this.debouncedCheckRecordInvitation.cancel();
    // Fire-and-forget cleanup of any still-pending preview row. The periodic
    // DestroyAbandonedFaxPreviewsWorker is the authoritative backstop, so we
    // don't await this and we don't surface errors.
    this.cleanupPreview();
  }

  get recordId() {
    return this.props.modalType?.data?.recordId;
  }

  // Drop setState calls that resolve after the modal closes so they don't
  // warn and don't swallow error toasts the user should still see.
  safeSetState = (update) => {
    if (this.mounted) this.setState(update);
  };

  canInvite = () => {
    const {
      existingCheck,
      recordCheck,
      sentInvitation,
      reinstated,
    } = this.state;
    return !sentInvitation
      && !reinstated
      && !recordCheck?.record_has_pending_invitation
      && !this.existingPartnerCheck()
      && !this.partnerConflictCheck()
      && !this.faxMismatchCheck()
      && !existingCheck?.has_pending_invitation
      && !existingCheck?.already_joined
      && !existingCheck?.has_accepted_invitation
      // Reinstate and Invite are mutually exclusive UX paths — the server
      // would 409 on an invite for a rejected partner's email, so hide the
      // invite form when the modal is offering "Grant Access" instead.
      && !existingCheck?.can_reinstate;
  };

  isEmailValid = () => {
    const { email } = this.state;
    return validSendEmail(email.trim());
  };

  isEmailAcceptable = () => {
    const { email } = this.state;
    if (this.isFaxDelivery() && email.trim().length === 0) return true;

    return this.isEmailValid();
  };

  canSubmitInvite = () => {
    const { faxNumber, loading } = this.state;
    if (loading) return false;
    if (!this.isFaxDelivery()) return this.isEmailValid();

    return this.isEmailAcceptable() && !!normalizeFaxNumber(faxNumber);
  };

  faxDeliveryAvailable = () => {
    const { cachedFaxDeliveryAvailable, existingCheck, recordCheck } = this.state;
    if (cachedFaxDeliveryAvailable === true) return true;
    return existingCheck?.fax_delivery_available === true || recordCheck?.fax_delivery_available === true;
  };

  isFaxDelivery = () => this.state.deliveryMethod === 'fax';

  canResendExistingInvitation = (invitation) => canResendInvitation(invitation, {
    faxDeliveryAvailable: this.faxDeliveryAvailable(),
  });

  handleChange = (field) => (event) => {
    const { value } = event.target;
    const isEmailEdit = field === 'email';
    const isFaxEdit = field === 'faxNumber';
    const clearsInviteState = isEmailEdit || isFaxEdit;
    // Any form edit makes a previously-generated preview stale; drop it.
    this.cleanupPreview();
    this.setState((prevState) => ({
      [field]: value,
      error: null,
      sentInvitation: clearsInviteState ? null : prevState.sentInvitation,
      deliveryFailed: clearsInviteState ? false : prevState.deliveryFailed,
      inviteLink: clearsInviteState ? null : prevState.inviteLink,
      reinstated: isEmailEdit ? false : prevState.reinstated,
      faxNumberTouched: isFaxEdit ? true : prevState.faxNumberTouched,
      // Clear so a stale can_reinstate decision cannot be acted on
      // for a different email while a new check is in flight.
      existingCheck: isEmailEdit ? null : prevState.existingCheck,
      // checkExisting populates recordCheck.existing_invitation from the
      // email-scoped response too, so the warning banner / Resend / Revoke
      // buttons could otherwise act on a previously-resolved invitation
      // belonging to a different email. Clear here and let
      // checkRecordInvitation refill record-only data.
      recordCheck: isEmailEdit ? null : prevState.recordCheck,
    }), () => {
      if (isEmailEdit) this.debouncedCheckRecordInvitation();
    });
  };

  handleCheckboxChange = (field) => (event) => {
    this.setState({ [field]: event.target.checked });
  };

  handleDeliveryMethodChange = (event) => {
    const { value } = event.target;
    // Switching delivery method invalidates any pending preview.
    this.cleanupPreview();
    this.setState((prevState) => ({
      deliveryMethod: value,
      error: null,
      faxNumber: value === 'fax' ? prevState.faxNumber : '',
    }));
  };

  checkRecordInvitation = async () => {
    if (!this.recordId) return;

    this.setState({ checking: true });
    try {
      const response = await axios.get('/api/v1/external_partner_invitations/check', {
        params: { record_id: this.recordId },
        headers: csrfHeaders(),
      });
      this.safeSetState((prevState) => ({
        recordCheck: response.data,
        cachedFaxDeliveryAvailable: response.data?.fax_delivery_available === true
          ? true
          : prevState.cachedFaxDeliveryAvailable,
        deliveryMethod: response.data?.fax_delivery_available === false && prevState.deliveryMethod === 'fax'
          ? 'email'
          : prevState.deliveryMethod,
        faxNumber: InvitePartnerModal.deriveFaxNumberFromCheck(prevState, response.data),
      }));
    } catch {
      this.safeSetState({ recordCheck: null });
    } finally {
      this.safeSetState({ checking: false });
    }
  };

  checkExisting = async () => {
    const { email } = this.state;
    if (!this.isEmailValid()) return;

    // If the user edits the email between blur and response, the late reply
    // must not overwrite state for a different email — otherwise Grant Access
    // / can_reinstate could fire against whatever address is typed now.
    const requestedEmail = email;
    this.setState({ checking: true });
    try {
      const response = await axios.get('/api/v1/external_partner_invitations/check', {
        params: {
          email,
          record_id: this.recordId,
        },
        headers: csrfHeaders(),
      });
      if (this.state.email !== requestedEmail) return;
      this.safeSetState((prevState) => ({
        existingCheck: response.data,
        recordCheck: response.data,
        cachedFaxDeliveryAvailable: response.data?.fax_delivery_available === true
          ? true
          : prevState.cachedFaxDeliveryAvailable,
        deliveryMethod: response.data?.fax_delivery_available === false && prevState.deliveryMethod === 'fax'
          ? 'email'
          : prevState.deliveryMethod,
        faxNumber: InvitePartnerModal.deriveFaxNumberFromCheck(prevState, response.data),
      }));
    } catch {
      if (this.state.email !== requestedEmail) return;
      this.safeSetState({ existingCheck: null });
    } finally {
      this.safeSetState({ checking: false });
    }
  };

  setConflictState = (error) => {
    const errorData = error.response?.data || {};
    const existingInvitation = errorData.existing_invitation;
    const hasExistingInvitation = !!existingInvitation;
    const hasAcceptedInvitation = existingInvitation?.status === 'accepted';
    const conflictCheck = {
      has_pending_invitation: hasExistingInvitation && !hasAcceptedInvitation,
      existing_invitation: existingInvitation,
      already_joined: !hasExistingInvitation,
      has_accepted_invitation: hasAcceptedInvitation,
    };

    this.safeSetState((prevState) => ({
      existingCheck: conflictCheck,
      recordCheck: errorData.reason === 'record_pending_invitation' && !hasAcceptedInvitation
        ? {
          record_has_pending_invitation: true,
          existing_invitation: existingInvitation,
        }
        : prevState.recordCheck,
    }));
  };

  existingPartnerCheck = () => {
    const { existingCheck, recordCheck } = this.state;
    return existingCheck?.existing_partner || recordCheck?.existing_partner || null;
  };

  selectedCheck = () => {
    const { existingCheck, recordCheck } = this.state;
    return existingCheck?.existing_partner ? existingCheck : recordCheck;
  };

  partnerConflictCheck = () => {
    const { existingCheck, recordCheck } = this.state;
    if (existingCheck?.partner_conflict) return existingCheck;
    if (recordCheck?.partner_conflict) return recordCheck;

    return null;
  };

  faxMismatchCheck = () => {
    const { existingCheck, recordCheck } = this.state;
    if (existingCheck?.fax_number_mismatch) return existingCheck;
    if (recordCheck?.fax_number_mismatch) return recordCheck;

    return null;
  };

  handleShareExistingPartner = async () => {
    const partner = this.existingPartnerCheck();
    if (!partner?.id || !this.recordId) return;

    const { addFaxForFutureMatching } = this.state;
    const check = this.selectedCheck() || {};

    this.setState({ loading: true, error: null });
    try {
      await axios.post(`/api/v1/records/${this.recordId}/shares`, {
        external_partner_id: partner.id,
        attach_sender_fax: !!check.can_attach_sender_fax && addFaxForFutureMatching,
      }, {
        headers: csrfHeaders(),
      });

      message.success({
        content: partner.joined
          ? 'Referral shared successfully'
          : 'Referral access queued until the partner accepts the invitation',
      });
      if (typeof this.props.onShareCreated === 'function') {
        this.props.onShareCreated();
      }
      this.props.hideModal();
    } catch (error) {
      this.safeSetState({
        error: error.response?.data?.error || 'Failed to share referral',
      });
    } finally {
      this.safeSetState({ loading: false });
    }
  };

  handleSendInvite = async () => {
    const {
      email,
      name,
      companyName,
      deliveryMethod,
      faxNumber,
    } = this.state;
    if (!this.isEmailAcceptable()) return;

    const normalizedFaxNumber = this.isFaxDelivery() ? normalizeFaxNumber(faxNumber) : null;
    if (this.isFaxDelivery() && !normalizedFaxNumber) {
      this.setState({ error: INVALID_FAX_NUMBER_ERROR });
      return;
    }

    const { addFaxForFutureMatching, recordCheck } = this.state;
    const attachSenderFax = !!recordCheck?.can_attach_sender_fax && addFaxForFutureMatching;

    this.setState({ loading: true, error: null });

    try {
      const response = await axios.post('/api/v1/external_partner_invitations', {
        invitation: {
          email: email.trim(),
          name: name.trim(),
          company_name: companyName.trim(),
          record_id: this.recordId,
          delivery_method: deliveryMethod,
          ...(this.isFaxDelivery() ? { fax_number: normalizedFaxNumber } : {}),
          ...(attachSenderFax ? { attach_sender_fax: true } : {}),
        },
      }, {
        headers: csrfHeaders(),
      });

      const deliveryFailed = response.data?.delivery_failed === true;
      if (!deliveryFailed) {
        message.success({
          content: this.isFaxDelivery()
            ? 'Fax invitation queued successfully'
            : 'Invitation sent successfully',
        });
      }
      this.safeSetState({
        sentInvitation: response.data.invitation,
        deliveryFailed,
        recordCheck: {
          record_has_pending_invitation: true,
          existing_invitation: response.data.invitation,
        },
      });
    } catch (error) {
      if (error.response?.status === 409) {
        this.setConflictState(error);
      }

      this.safeSetState({
        error: error.response?.data?.errors?.[0]
          || error.response?.data?.error
          || 'Failed to send invitation',
      });
    } finally {
      this.safeSetState({ loading: false });
    }
  };

  handleGenerateLink = async () => {
    const { email, name, companyName } = this.state;
    if (!this.isEmailValid()) return;

    this.setState({ loading: true, error: null });

    try {
      const response = await axios.post('/api/v1/external_partner_invitations/generate_link', {
        invitation: {
          email: email.trim(),
          name: name.trim(),
          company_name: companyName.trim(),
          record_id: this.recordId,
        },
      }, {
        headers: csrfHeaders(),
      });

      this.safeSetState({ inviteLink: response.data.invitation_url });
    } catch (error) {
      if (error.response?.status === 409) {
        this.setConflictState(error);
      }

      this.safeSetState({
        error: error.response?.data?.error || 'Failed to generate link',
      });
    } finally {
      this.safeSetState({ loading: false });
    }
  };

  handleGeneratePreview = async () => {
    const previewRequest = this.previewRequestSnapshot();
    const {
      email, name, companyName, faxNumber,
    } = previewRequest;

    if (!faxNumber) {
      this.setState({ error: INVALID_FAX_NUMBER_ERROR });
      return;
    }

    // Drop any prior preview row before generating a new one — otherwise
    // re-clicking Generate Preview without editing the form would leave the
    // old invitation row in the DB until the cleanup worker reaps it.
    this.cleanupPreview();

    this.setState({ generatingPreview: true, error: null });

    try {
      const { addFaxForFutureMatching, recordCheck } = this.state;
      const attachSenderFax = !!recordCheck?.can_attach_sender_fax && addFaxForFutureMatching;
      const response = await axios.post(
        '/api/v1/external_partner_invitations/preview_fax',
        {
          invitation: {
            email,
            name,
            company_name: companyName,
            record_id: previewRequest.recordId,
            fax_number: faxNumber,
            ...(attachSenderFax ? { attach_sender_fax: true } : {}),
          },
        },
        {
          headers: { ...csrfHeaders(), Accept: 'application/pdf' },
          responseType: 'arraybuffer',
        },
      );

      const invitationId = response.headers['x-invitation-id'];
      if (!this.previewRequestMatches(previewRequest)) {
        this.destroyPreviewById(invitationId);
        return;
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      // Browsers allow window.open from a click-initiated async handler, but
      // popup blockers can still reject it. If they do, surface a clear error
      // instead of leaving the user thinking the preview button is broken.
      const newTab = window.open(blobUrl, '_blank', 'noopener');
      if (!newTab) {
        window.URL.revokeObjectURL(blobUrl);
        this.destroyPreviewById(invitationId);
        this.safeSetState({
          previewedInvitationId: null,
          error: 'Pop-up blocked. Allow pop-ups for this site and click Generate Preview again.',
        });
        return;
      }
      // Revoke after a generous delay so the new tab has time to finish loading.
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
      this.safeSetState({ previewedInvitationId: invitationId ? Number(invitationId) : null });
    } catch (error) {
      // Error bodies come back as arraybuffer; decode if present.
      let errorMessage = 'Failed to generate preview';
      if (error.response?.data) {
        try {
          const decoded = new TextDecoder().decode(new Uint8Array(error.response.data));
          const parsed = JSON.parse(decoded);
          if (error.response.status === 409) {
            this.setConflictState({ response: { data: parsed } });
          }
          errorMessage = parsed.errors?.[0] || parsed.error || errorMessage;
        } catch {
          // Fall through to default message.
        }
      }
      this.safeSetState({ error: errorMessage });
    } finally {
      this.safeSetState({ generatingPreview: false });
    }
  };

  handleSendPreviewedFax = async () => {
    const { previewedInvitationId } = this.state;
    if (!previewedInvitationId) return;

    this.setState({ loading: true, error: null, sendingPreviewedFax: true });

    try {
      const response = await axios.post(
        `/api/v1/external_partner_invitations/${previewedInvitationId}/send_fax`,
        {},
        { headers: csrfHeaders() },
      );

      const deliveryFailed = response.data?.delivery_failed === true;
      if (deliveryFailed) {
        message.error({ content: 'Invitation saved, but delivery failed. Try resending below.' });
      } else {
        message.success({ content: 'Fax invitation queued successfully' });
      }
      this.safeSetState({
        sentInvitation: response.data.invitation,
        deliveryFailed,
        previewedInvitationId: null,
        recordCheck: {
          record_has_pending_invitation: true,
          existing_invitation: response.data.invitation,
        },
      });
    } catch (error) {
      if (error.response?.status === 409) {
        this.cleanupPreview({ force: true });
        this.setConflictState(error);
        return;
      }

      this.cleanupPreview({ force: true });
      this.safeSetState({
        error: error.response?.data?.error || 'Failed to send fax invitation',
      });
    } finally {
      this.safeSetState({ loading: false, sendingPreviewedFax: false });
    }
  };

  // Fire-and-forget DELETE of any pending preview row. Used on form edit,
  // delivery-method change, and componentWillUnmount. The periodic
  // DestroyAbandonedFaxPreviewsWorker is the authoritative backstop.
  cleanupPreview = ({ force = false } = {}) => {
    const { previewedInvitationId, sendingPreviewedFax } = this.state;
    if (sendingPreviewedFax && !force) return;
    if (!previewedInvitationId) return;

    this.safeSetState({ previewedInvitationId: null });
    this.destroyPreviewById(previewedInvitationId);
  };

  destroyPreviewById = (previewId) => {
    if (!previewId) return;

    axios.delete(
      `/api/v1/external_partner_invitations/${previewId}/preview`,
      { headers: csrfHeaders() },
    ).catch(() => {});
  };

  previewRequestSnapshot = () => {
    const {
      deliveryMethod, email, name, companyName, faxNumber,
    } = this.state;

    return {
      deliveryMethod,
      email: email.trim(),
      name: name.trim(),
      companyName: companyName.trim(),
      faxNumber: normalizeFaxNumber(faxNumber),
      recordId: this.recordId,
    };
  };

  previewRequestMatches = (previewRequest) => {
    const currentRequest = this.previewRequestSnapshot();
    return currentRequest.deliveryMethod === previewRequest.deliveryMethod
      && currentRequest.email === previewRequest.email
      && currentRequest.name === previewRequest.name
      && currentRequest.companyName === previewRequest.companyName
      && currentRequest.faxNumber === previewRequest.faxNumber
      && currentRequest.recordId === previewRequest.recordId;
  };

  existingInvitation = () => {
    const { sentInvitation, recordCheck, existingCheck } = this.state;
    return sentInvitation || recordCheck?.existing_invitation || existingCheck?.existing_invitation || null;
  };

  handleReinstate = async () => {
    const { existingCheck, email } = this.state;
    if (!existingCheck?.can_reinstate) return;

    // If the user edits the email between click and response, the late reply
    // must not paint a "Access granted" success on an unrelated address.
    const requestedEmail = email;
    this.setState({ loading: true, error: null });
    try {
      await axios.post(
        '/api/v1/external_partner_approvals/reinstate',
        { email: email.trim() },
        { headers: csrfHeaders() },
      );
      if (this.state.email !== requestedEmail) return;
      this.safeSetState({ reinstated: true, existingCheck: null });
    } catch (error) {
      if (this.state.email !== requestedEmail) return;
      this.safeSetState({
        error: error.response?.data?.error || 'Failed to grant access',
      });
    } finally {
      this.safeSetState({ loading: false });
    }
  };

  syncInvitationState = (updatedInvitation) => {
    if (!updatedInvitation?.id) return;

    this.safeSetState((prevState) => ({
      sentInvitation: prevState.sentInvitation?.id === updatedInvitation.id
        ? updatedInvitation
        : prevState.sentInvitation,
      recordCheck: prevState.recordCheck?.existing_invitation?.id === updatedInvitation.id
        ? {
          ...prevState.recordCheck,
          existing_invitation: updatedInvitation,
        }
        : prevState.recordCheck,
      existingCheck: prevState.existingCheck?.existing_invitation?.id === updatedInvitation.id
        ? {
          ...prevState.existingCheck,
          existing_invitation: updatedInvitation,
        }
        : prevState.existingCheck,
    }));
  };

  handleResend = async () => {
    const invitation = this.existingInvitation();
    const invitationId = invitation?.id;
    if (!this.canResendExistingInvitation(invitation)) return;
    if (!invitationId) return;

    this.setState({ loading: true, error: null });

    try {
      const response = await axios.post(
        `/api/v1/external_partner_invitations/${invitationId}/resend`,
        {},
        { headers: csrfHeaders() },
      );
      this.syncInvitationState(response.data?.invitation);
      const deliveryFailed = response.data?.delivery_failed === true;
      this.safeSetState({ deliveryFailed });
      if (deliveryFailed) {
        message.error({ content: 'Invitation saved, but delivery failed. Try resending below.' });
      } else {
        message.success({ content: 'Invitation resent successfully' });
      }
    } catch (error) {
      this.safeSetState({
        error: error.response?.data?.error || 'Failed to resend invitation',
      });
    } finally {
      this.safeSetState({ loading: false });
    }
  };

  handleRevoke = async () => {
    const invitation = this.existingInvitation();
    const invitationId = invitation?.id;
    if (!isActionablePendingInvitation(invitation)) return;
    if (!invitationId) return;

    this.setState({ loading: true, error: null });

    try {
      await axios.delete(
        `/api/v1/external_partner_invitations/${invitationId}/revoke`,
        { headers: csrfHeaders() },
      );
      message.success({ content: 'Invitation revoked successfully' });
      this.safeSetState({
        sentInvitation: null,
        existingCheck: null,
        recordCheck: null,
      });
      await this.checkRecordInvitation();
      await this.checkExisting();
    } catch (error) {
      this.safeSetState({
        error: error.response?.data?.error || 'Failed to revoke invitation',
      });
    } finally {
      this.safeSetState({ loading: false });
    }
  };

  copyToClipboard = async () => {
    const { inviteLink } = this.state;
    if (!inviteLink) return;

    try {
      await navigator.clipboard.writeText(inviteLink);
      this.safeSetState({ copied: true });
      this.copyTimer = setTimeout(() => this.safeSetState({ copied: false }), 2000);
    } catch {
      message.error({ content: 'Failed to copy link' });
    }
  };

  renderWarning = () => {
    const {
      checking, existingCheck, recordCheck, reinstated,
    } = this.state;
    const mismatchCheck = this.faxMismatchCheck();
    const conflictCheck = this.partnerConflictCheck();
    const existingPartner = this.existingPartnerCheck();
    const selectedCheck = this.selectedCheck() || {};

    if (reinstated) {
      return (
        <div
          className="invite-partner-modal__warning invite-partner-modal__warning--success"
          role="status"
          aria-live="polite"
        >
          <Check size={18} />
          <Typography variant="text" size="small">
            Access granted. This partner can now log in and view shared referrals.
          </Typography>
        </div>
      );
    }

    if (checking) {
      return (
        <div
          className="invite-partner-modal__status-row"
          role="status"
          aria-live="polite"
        >
          <Spin size="small" />
          <Typography variant="text" size="xsmall">Checking existing invitations...</Typography>
        </div>
      );
    }

    if (mismatchCheck) {
      return (
        <div
          className="invite-partner-modal__warning invite-partner-modal__warning--danger"
          role="alert"
          aria-live="assertive"
        >
          <WarningCircle size={18} />
          <Typography variant="text" size="small">
            {mismatchCheck.sender_fax
              ? `Fax matching is limited to this referral's sender fax (${mismatchCheck.sender_fax}). Clear the fax field or use that number.`
              : 'This referral does not have a sender fax to match. Clear the fax field to continue.'}
          </Typography>
        </div>
      );
    }

    if (conflictCheck) {
      return (
        <div
          className="invite-partner-modal__warning invite-partner-modal__warning--danger"
          role="alert"
          aria-live="assertive"
        >
          <WarningCircle size={18} />
          <div className="invite-partner-modal__warning-content">
            <Typography variant="text" size="small">
              This email and fax number match different existing partners.
            </Typography>
            {this.renderPartnerEvidence(conflictCheck)}
            <Typography variant="text" size="xsmall">
              Correct the partner details before sharing.
            </Typography>
          </div>
        </div>
      );
    }

    if (existingPartner) {
      const partnerLabel = existingPartner.company_name || existingPartner.email || 'this partner';
      const pendingInvitation = selectedCheck.existing_invitation;
      const actionableInvitation = isActionablePendingInvitation(pendingInvitation);
      const canResend = this.canResendExistingInvitation(pendingInvitation);

      return (
        <div
          className="invite-partner-modal__warning invite-partner-modal__warning--success"
          role="status"
          aria-live="polite"
        >
          <Check size={18} />
          <div className="invite-partner-modal__warning-content">
            <Typography variant="text" size="small">
              {existingPartner.joined
                ? `Existing partner found: ${partnerLabel}.`
                : `Pending partner found: ${partnerLabel}.`}
            </Typography>
            <InvitationDeliveryBadges
              invitation={pendingInvitation}
              className="invite-partner-modal__invitation-badges"
            />
            {selectedCheck.can_attach_sender_fax && (
              <label className="invite-partner-modal__checkbox-row" htmlFor="addFaxForFutureMatching">
                <input
                  id="addFaxForFutureMatching"
                  type="checkbox"
                  checked={this.state.addFaxForFutureMatching}
                  onChange={this.handleCheckboxChange('addFaxForFutureMatching')}
                />
                <Typography variant="text" size="small">
                  {`Add ${selectedCheck.attachable_fax_number} to this partner for future matching`}
                </Typography>
              </label>
            )}
            <div className="invite-partner-modal__warning-actions">
              <Button type="primary" onClick={this.handleShareExistingPartner} loading={this.state.loading}>
                {existingPartner.joined ? 'Share with existing partner' : 'Share with pending partner'}
              </Button>
              {canResend && (
                <Button type="link" onClick={this.handleResend}>
                  Resend invitation
                </Button>
              )}
              {actionableInvitation && (
                <Button type="link" onClick={this.handleRevoke}>
                  Revoke invitation
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (selectedCheck.fax_ambiguous && !this.isEmailValid()) {
      return (
        <div
          className="invite-partner-modal__warning invite-partner-modal__warning--warning"
          role="status"
          aria-live="polite"
        >
          <WarningCircle size={18} />
          <div className="invite-partner-modal__warning-content">
            <Typography variant="text" size="small">
              This fax number matches multiple existing partners.
            </Typography>
            <Typography variant="text" size="xsmall">
              Enter the partner email to choose the correct partner, or correct the saved partner fax numbers.
            </Typography>
          </div>
        </div>
      );
    }

    if (recordCheck?.record_has_pending_invitation) {
      const pendingEmail = recordCheck?.existing_invitation?.email;
      const actionableInvitation = isActionablePendingInvitation(recordCheck?.existing_invitation);
      const canResend = this.canResendExistingInvitation(recordCheck?.existing_invitation);
      return (
        <div
          className="invite-partner-modal__warning invite-partner-modal__warning--warning"
          role="status"
          aria-live="polite"
        >
          <WarningCircle size={18} />
          <div className="invite-partner-modal__warning-content">
            <Typography variant="text" size="small">
              {pendingEmail
                ? `This referral already has a pending invitation for ${pendingEmail}.`
                : 'This referral already has a pending invitation.'}
            </Typography>
            <InvitationDeliveryBadges
              invitation={recordCheck?.existing_invitation}
              className="invite-partner-modal__invitation-badges"
            />
            <div className="invite-partner-modal__warning-actions">
              {canResend && (
                <Button type="link" onClick={this.handleResend}>
                  Resend invitation
                </Button>
              )}
              {actionableInvitation && (
                <Button type="link" onClick={this.handleRevoke}>
                  Revoke invitation
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (existingCheck?.can_reinstate) {
      return (
        <div
          className="invite-partner-modal__warning invite-partner-modal__warning--warning"
          role="status"
          aria-live="polite"
        >
          <WarningCircle size={18} />
          <div className="invite-partner-modal__warning-content">
            <Typography variant="text" size="small">
              This email accepted a previous invitation but was denied access. Grant them access now?
            </Typography>
            <div className="invite-partner-modal__warning-actions">
              <Button type="primary" size="small" loading={this.state.loading} onClick={this.handleReinstate}>
                Grant Access
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (existingCheck?.already_joined) {
      return (
        <div
          className="invite-partner-modal__warning invite-partner-modal__warning--danger"
          role="status"
          aria-live="polite"
        >
          <WarningCircle size={18} />
          <Typography variant="text" size="small">
            This email is already registered on the platform.
          </Typography>
        </div>
      );
    }

    if (existingCheck?.has_accepted_invitation) {
      return (
        <div
          className="invite-partner-modal__warning invite-partner-modal__warning--success"
          role="status"
          aria-live="polite"
        >
          <Check size={18} />
          <Typography variant="text" size="small">
            This email already accepted a previous invitation.
          </Typography>
        </div>
      );
    }

    if (existingCheck?.has_pending_invitation) {
      const actionableInvitation = isActionablePendingInvitation(existingCheck?.existing_invitation);
      const canResend = this.canResendExistingInvitation(existingCheck?.existing_invitation);
      return (
        <div
          className="invite-partner-modal__warning invite-partner-modal__warning--warning"
          role="status"
          aria-live="polite"
        >
          <WarningCircle size={18} />
          <div className="invite-partner-modal__warning-content">
            <Typography variant="text" size="small">
              A pending invitation already exists for this email.
            </Typography>
            <InvitationDeliveryBadges
              invitation={existingCheck?.existing_invitation}
              className="invite-partner-modal__invitation-badges"
            />
            <div className="invite-partner-modal__warning-actions">
              {canResend && (
                <Button type="link" onClick={this.handleResend}>
                  Resend invitation
                </Button>
              )}
              {actionableInvitation && (
                <Button type="link" onClick={this.handleRevoke}>
                  Revoke invitation
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  renderPartnerEvidence = (check) => {
    const partners = check.conflicting_partners || [];
    const emailPartners = partners.filter((partner) => partner.match_type === 'email' || partner.match_type === 'email_and_fax');
    const faxPartners = partners.filter((partner) => partner.match_type === 'fax' || partner.match_type === 'email_and_fax');

    return (
      <div className="invite-partner-modal__match-evidence">
        {emailPartners.length > 0 && (
          <Typography variant="text" size="xsmall">
            {`Email matches: ${emailPartners.map(this.partnerDisplayName).join(', ')}`}
          </Typography>
        )}
        {faxPartners.length > 0 && (
          <Typography variant="text" size="xsmall">
            {`Fax matches: ${faxPartners.map(this.partnerDisplayName).join(', ')}`}
          </Typography>
        )}
      </div>
    );
  };

  partnerDisplayName = (partner) => partner.company_name || partner.email || `Partner ${partner.id}`;

  renderSentSection = () => {
    const { hideModal } = this.props;
    const { loading, deliveryFailed } = this.state;
    const invitation = this.existingInvitation();
    const actionableInvitation = isActionablePendingInvitation(invitation);
    const canResend = this.canResendExistingInvitation(invitation);
    const destination = invitation?.delivery_method === 'fax'
      ? invitation?.fax_number || 'partner'
      : invitation?.email || 'partner';
    const successMessage = invitation?.delivery_method === 'fax'
      ? `Fax invitation queued for ${destination}.`
      : `Invitation sent to ${destination}.`;
    const failureMessage = 'Invitation saved, but delivery failed. Try resending below.';
    let helperMessage = 'This referral now has an invitation.';
    if (canResend) {
      helperMessage = 'This referral now has a pending invitation. You can resend it until it is accepted, revoked, or expired.';
    } else if (actionableInvitation) {
      helperMessage = 'This referral now has a pending invitation. You can revoke it while delivery is still in progress.';
    }

    return (
      <div className="invite-partner-modal__sent-section">
        {deliveryFailed ? (
          <div
            className="invite-partner-modal__warning invite-partner-modal__warning--danger"
            role="alert"
            aria-live="assertive"
          >
            <WarningCircle size={18} />
            <Typography variant="text" size="small">
              {failureMessage}
            </Typography>
          </div>
        ) : (
          <div
            className="invite-partner-modal__warning invite-partner-modal__warning--success"
            role="status"
            aria-live="polite"
          >
            <Check size={18} />
            <Typography variant="text" size="small">
              {successMessage}
            </Typography>
          </div>
        )}
        <InvitationDeliveryBadges
          invitation={invitation}
          className="invite-partner-modal__invitation-badges"
        />
        <Typography variant="text" size="small">
          {helperMessage}
        </Typography>
        <div className="invite-partner-modal__actions-row">
          {canResend && (
            <Button onClick={this.handleResend} loading={loading}>
              Resend invitation
            </Button>
          )}
          {actionableInvitation && (
            <Button onClick={this.handleRevoke} loading={loading}>
              Revoke invitation
            </Button>
          )}
          <Button type="primary" onClick={hideModal}>
            Done
          </Button>
        </div>
      </div>
    );
  };

  renderLinkSection = () => {
    const { copied, inviteLink } = this.state;

    return (
      <div className="invite-partner-modal__link-section">
        <Typography variant="text" size="small">
          Share this invite link directly with the partner.
        </Typography>

        <div className="invite-partner-modal__link-box">
          <input type="text" readOnly value={inviteLink} className="invite-partner-modal__link-input" />
          <Button onClick={this.copyToClipboard}>
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {' '}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>

        <Typography variant="text" size="xsmall" className="invite-partner-modal__link-note">
          This link expires in 10 days.
        </Typography>
      </div>
    );
  };

  renderForm = () => {
    const {
      email,
      name,
      companyName,
      deliveryMethod,
      faxNumber,
      loading,
      error,
      previewedInvitationId,
      generatingPreview,
    } = this.state;
    const faxDeliveryAvailable = this.faxDeliveryAvailable();
    const primaryActionLabel = deliveryMethod === 'fax' ? 'Send Fax' : 'Send Invitation';
    const PrimaryActionIcon = deliveryMethod === 'fax' ? Phone : EnvelopeSimple;
    const emailRequired = deliveryMethod !== 'fax';
    const canSubmitInvite = this.canSubmitInvite();
    const isFax = this.isFaxDelivery();
    const canGeneratePreview = isFax
      && !!normalizeFaxNumber(faxNumber)
      && this.isEmailAcceptable();
    const primaryActionHandler = previewedInvitationId
      ? this.handleSendPreviewedFax
      : this.handleSendInvite;

    return (
      <>
        <div className="invite-partner-modal__form">
          <Input
            label="Email Address"
            required={emailRequired}
            type="email"
            value={email}
            onChange={this.handleChange('email')}
            onBlur={this.checkExisting}
            placeholder="partner@example.com"
          />

          {this.renderWarning()}

          {this.canInvite() && (
            <>
              {faxDeliveryAvailable && (
                <div className="invite-partner-modal__delivery-method">
                  <Typography
                    variant="text"
                    size="xsmall"
                    className="invite-partner-modal__delivery-label"
                  >
                    Delivery Method
                  </Typography>
                  <Radio.Group
                    className="invite-partner-modal__delivery-group"
                    onChange={this.handleDeliveryMethodChange}
                    value={deliveryMethod}
                  >
                    <Radio value="email">Email</Radio>
                    <Radio value="fax">Fax</Radio>
                  </Radio.Group>
                </div>
              )}

              {deliveryMethod === 'fax' && (
                <>
                  <Input
                    label="Fax Number"
                    required
                    value={faxNumber}
                    onChange={this.handleChange('faxNumber')}
                    placeholder="+15551234567"
                    status={error === INVALID_FAX_NUMBER_ERROR ? 'error' : ''}
                  />
                  {error === INVALID_FAX_NUMBER_ERROR && (
                    <Typography
                      variant="text"
                      size="small"
                      className="invite-partner-modal__error invite-partner-modal__error--field"
                    >
                      {error}
                    </Typography>
                  )}
                </>
              )}

              <Input
                label="Contact Name"
                value={name}
                onChange={this.handleChange('name')}
                placeholder="Dr. Jane Smith"
              />

              <Input
                label="Company Name"
                value={companyName}
                onChange={this.handleChange('companyName')}
                placeholder="Smith Medical Group"
              />
            </>
          )}

          {error && error !== INVALID_FAX_NUMBER_ERROR && (
            <Typography variant="text" size="small" className="invite-partner-modal__error">
              {error}
            </Typography>
          )}
        </div>

        {this.canInvite() && this.state.recordCheck?.can_attach_sender_fax && (
          <label
            className="invite-partner-modal__checkbox-row"
            htmlFor="addFaxForFutureMatchingInvite"
          >
            <input
              id="addFaxForFutureMatchingInvite"
              type="checkbox"
              checked={this.state.addFaxForFutureMatching}
              onChange={this.handleCheckboxChange('addFaxForFutureMatching')}
            />
            <Typography variant="text" size="small">
              {`Add ${this.state.recordCheck.attachable_fax_number} to this partner for future matching`}
            </Typography>
          </label>
        )}

        {this.canInvite() && (
          <>
            {isFax && previewedInvitationId && (
              <Typography
                variant="text"
                size="xsmall"
                className="invite-partner-modal__preview-hint"
              >
                Preview downloaded — review the PDF, then click Send Fax.
              </Typography>
            )}
            <div className="invite-partner-modal__actions-row">
              {isFax ? (
                <Button
                  disabled={!canGeneratePreview || generatingPreview || loading}
                  loading={generatingPreview}
                  onClick={this.handleGeneratePreview}
                >
                  <FilePdf size={16} className="invite-partner-modal__action-icon" />
                  Generate Preview
                </Button>
              ) : (
                <Button disabled={!this.isEmailValid() || loading} onClick={this.handleGenerateLink}>
                  <LinkSimple size={16} className="invite-partner-modal__action-icon" />
                  Generate Link
                </Button>
              )}
              <Button
                type="primary"
                disabled={!canSubmitInvite || generatingPreview}
                loading={loading}
                onClick={primaryActionHandler}
              >
                <PrimaryActionIcon size={16} className="invite-partner-modal__action-icon" />
                {primaryActionLabel}
              </Button>
            </div>
          </>
        )}
      </>
    );
  };

  render() {
    const { hideModal } = this.props;
    const {
      inviteLink,
      sentInvitation,
    } = this.state;

    let modalBody = this.renderForm();
    if (inviteLink) {
      modalBody = this.renderLinkSection();
    } else if (sentInvitation) {
      modalBody = this.renderSentSection();
    }

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={{ width: '460px' }}
        dismiss="Close"
        modalTitle="Invite External Partner"
        wrapClassName="invite-partner-modal__wrapper"
        isDraggable
      >
        <div className="invite-partner-modal">
          {modalBody}
        </div>
      </ModalWrapper>
    );
  }
}

InvitePartnerModal.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object,
  onShareCreated: PropTypes.func,
};

InvitePartnerModal.defaultProps = {
  modalType: {},
  onShareCreated: null,
};

export default InvitePartnerModal;
